import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
  Modal,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import AppText from './AppText';
import AppButton from './AppButton';

const STORAGE_KEY = 'pwa-install-dismissed-at';
const DISMISS_DAYS = 7;

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{outcome: 'accepted' | 'dismissed'}>;
};

function isStandalone(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const nav = window.navigator as Navigator & {standalone?: boolean};
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    nav.standalone === true
  );
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function wasDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const ts = Number(raw);
    if (!Number.isFinite(ts)) {
      return false;
    }
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Web-only install prompt.
 * Chrome/Edge/Android: uses beforeinstallprompt.
 * iOS Safari: shows Add to Home Screen instructions (no install API).
 */
export default function InstallPwaPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(
    null,
  );
  const [iosMode, setIosMode] = useState(false);
  const gotNativePrompt = useRef(false);

  useEffect(() => {
    if (Platform.OS !== 'web' || isStandalone() || wasDismissedRecently()) {
      return;
    }

    const onBeforeInstall = (e: Event) => {
      e.preventDefault();
      gotNativePrompt.current = true;
      setDeferred(e as BeforeInstallPromptEvent);
      setIosMode(false);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Fallback after a short wait if the browser never offers a native prompt.
    const timer = window.setTimeout(() => {
      if (isStandalone() || wasDismissedRecently() || gotNativePrompt.current) {
        return;
      }
      if (isIos()) {
        setIosMode(true);
        setVisible(true);
      } else {
        setVisible(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.clearTimeout(timer);
    };
  }, []);

  const dismiss = useCallback(() => {
    markDismissed();
    setVisible(false);
  }, []);

  const install = useCallback(async () => {
    if (deferred) {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice.outcome === 'accepted') {
        setVisible(false);
        return;
      }
    }
    dismiss();
  }, [deferred, dismiss]);

  if (!visible) {
    return null;
  }

  const canNativeInstall = Boolean(deferred);

  return (
    <Modal transparent animationType="fade" visible={visible} onRequestClose={dismiss}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <AppText style={styles.title}>התקן את שליח פלוס</AppText>
          {iosMode ? (
            <AppText style={styles.body}>
              להתקנה כאייקון במסך הבית: לחץ על כפתור השיתוף בתחתית Safari, ואז בחר
              ״הוסף למסך הבית״.
            </AppText>
          ) : canNativeInstall ? (
            <AppText style={styles.body}>
              התקן את האפליקציה במכשיר שלך לגישה מהירה יותר, גם בלי לפתוח את הדפדפן.
            </AppText>
          ) : (
            <AppText style={styles.body}>
              ניתן להתקין את שליח פלוס כאפליקציה דרך תפריט הדפדפן (התקן אפליקציה / Install app).
            </AppText>
          )}

          <View style={styles.actions}>
            {canNativeInstall ? (
              <AppButton
                text="התקן עכשיו"
                width={220}
                height={44}
                fontSize={16}
                onPress={install}
              />
            ) : null}
            <TouchableOpacity onPress={dismiss} style={styles.later}>
              <AppText style={styles.laterText}>
                {iosMode || !canNativeInstall ? 'הבנתי' : 'מאוחר יותר'}
              </AppText>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  card: {
    width: '100%',
    maxWidth: 360,
    backgroundColor: '#fff',
    borderRadius: 16,
    paddingVertical: 28,
    paddingHorizontal: 22,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOpacity: 0.2,
    shadowRadius: 12,
    shadowOffset: {width: 0, height: 4},
    elevation: 6,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    color: '#019FB2',
    marginBottom: 12,
    textAlign: 'center',
  },
  body: {
    fontSize: 15,
    color: '#333',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 22,
  },
  actions: {
    width: '100%',
    alignItems: 'center',
  },
  later: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    marginTop: 8,
  },
  laterText: {
    color: '#666',
    fontSize: 15,
  },
});
